import { useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/feature/Reveal';
import { testimonials } from '@/mocks/testimonials';

const AUTOPLAY_MS = 4500;

function Stars({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, index) => (
        <i
          key={index}
          className={
            index < rating
              ? 'ri-star-fill text-sm text-accent-500'
              : 'ri-star-line text-sm text-background-300'
          }
          aria-hidden="true"
        ></i>
      ))}
    </div>
  );
}

export default function Testimonials() {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const indexRef = useRef(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [pageCount, setPageCount] = useState(1);

  const measureStep = useCallback(() => {
    const track = trackRef.current;
    if (!track) return 0;
    const cards = track.querySelectorAll<HTMLElement>('[data-slide]');
    if (cards.length === 0) return 0;
    if (cards.length === 1) return cards[0].offsetWidth;
    return cards[1].offsetLeft - cards[0].offsetLeft;
  }, []);

  const updateIndex = useCallback((index: number) => {
    indexRef.current = index;
    setActiveIndex(index);
  }, []);

  const getMaxIndex = useCallback(() => {
    const track = trackRef.current;
    const step = measureStep();
    if (!track || step <= 0) return 0;
    const visible = Math.max(1, Math.round(track.clientWidth / step));
    return Math.max(0, testimonials.length - visible);
  }, [measureStep]);

  const syncPageCount = useCallback(() => {
    const max = getMaxIndex();
    setPageCount(max + 1);
    if (indexRef.current > max) {
      updateIndex(max);
    }
  }, [getMaxIndex, updateIndex]);

  const scrollToIndex = useCallback(
    (index: number) => {
      const track = trackRef.current;
      if (!track) return;
      const step = measureStep();
      if (step <= 0) return;
      const max = getMaxIndex();
      const target = Math.min(Math.max(index, 0), max);
      const maxScroll = track.scrollWidth - track.clientWidth;
      track.scrollTo({ left: Math.min(target * step, maxScroll), behavior: 'smooth' });
      updateIndex(target);
    },
    [measureStep, getMaxIndex, updateIndex],
  );

  const goNext = useCallback(() => {
    const max = getMaxIndex();
    const next = indexRef.current >= max ? 0 : indexRef.current + 1;
    scrollToIndex(next);
  }, [getMaxIndex, scrollToIndex]);

  const goPrev = useCallback(() => {
    const max = getMaxIndex();
    const prev = indexRef.current <= 0 ? max : indexRef.current - 1;
    scrollToIndex(prev);
  }, [getMaxIndex, scrollToIndex]);

  useEffect(() => {
    if (paused) return undefined;
    const id = window.setInterval(goNext, AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [paused, goNext]);

  useEffect(() => {
    syncPageCount();
    const track = trackRef.current;
    if (!track || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', syncPageCount);
      return () => window.removeEventListener('resize', syncPageCount);
    }
    const observer = new ResizeObserver(syncPageCount);
    observer.observe(track);
    return () => observer.disconnect();
  }, [syncPageCount]);

  const handleScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const step = measureStep();
    if (step <= 0) return;
    const max = getMaxIndex();
    const index = Math.min(max, Math.max(0, Math.round(track.scrollLeft / step)));
    if (index !== indexRef.current) {
      updateIndex(index);
    }
  }, [measureStep, getMaxIndex, updateIndex]);

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        setPaused(true);
        goNext();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setPaused(true);
        goPrev();
      }
    },
    [goNext, goPrev],
  );

  return (
    <section className="py-16 md:py-24 bg-background-50">
      <div className="w-full px-4 md:px-8">
        <div className="max-w-shell mx-auto">
          <Reveal>
            <SectionHeading
              eyebrow="From real users"
              title="Traders who stopped gambling on gold"
              subtitle="No paid reviews, no affiliate hype. Just honest words from people who run the EA on their own accounts."
            />
          </Reveal>

          <Reveal delay={80}>
            <div className="mt-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
                <div className="flex items-center gap-2.5">
                  <span className="font-num text-2xl md:text-3xl font-bold text-foreground-950">4.8</span>
                  <div>
                    <Stars rating={5} />
                    <span className="block text-xs text-foreground-500">Average rating</span>
                  </div>
                </div>
                <span className="hidden sm:block w-px h-10 bg-background-200" aria-hidden="true"></span>
                <div className="flex items-center gap-2.5">
                  <span className="font-num text-2xl md:text-3xl font-bold text-foreground-950">1,900+</span>
                  <span className="text-xs text-foreground-500 max-w-[140px] leading-snug">
                    traders running the EA worldwide
                  </span>
                </div>
                <span className="hidden sm:block w-px h-10 bg-background-200" aria-hidden="true"></span>
                <div className="flex items-center gap-2.5">
                  <span className="font-num text-2xl md:text-3xl font-bold text-foreground-950">62</span>
                  <span className="text-xs text-foreground-500 max-w-[140px] leading-snug">
                    countries our chat has shipped the file to
                  </span>
                </div>
              </div>

              <div className="hidden sm:flex items-center gap-2.5 shrink-0">
                <button
                  type="button"
                  onClick={goPrev}
                  aria-label="Previous testimonials"
                  className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300 bg-background-50 text-foreground-700 hover:border-accent-500 hover:text-foreground-950 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line text-lg" aria-hidden="true"></i>
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  aria-label="Next testimonials"
                  className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300 bg-background-50 text-foreground-700 hover:border-accent-500 hover:text-foreground-950 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-line text-lg" aria-hidden="true"></i>
                </button>
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div
              ref={trackRef}
              onScroll={handleScroll}
              onKeyDown={handleKeyDown}
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
              onFocusCapture={() => setPaused(true)}
              onBlurCapture={() => setPaused(false)}
              onTouchStart={() => setPaused(true)}
              onTouchEnd={() => setPaused(false)}
              tabIndex={0}
              role="region"
              aria-roledescription="carousel"
              aria-label="User testimonials carousel"
              className="touch-momentum no-scrollbar mt-8 md:mt-10 flex gap-4 md:gap-5 overflow-x-auto snap-x snap-mandatory pb-1 -mx-4 px-4 md:mx-0 md:px-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 focus-visible:ring-offset-background-50 rounded-lg"
            >
              {testimonials.map((item) => (
                <div
                  key={item.id}
                  data-slide
                  className="snap-start flex-none w-[86%] sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.834rem)]"
                >
                  <figure className="h-full flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 md:p-6 hover:border-accent-300 transition-colors duration-200">
                    <div className="flex items-center justify-between">
                      <Stars rating={item.rating} />
                      <i className="ri-double-quotes-r text-accent-300 text-2xl" aria-hidden="true"></i>
                    </div>
                    <blockquote className="mt-4 flex-1 text-xs md:text-sm leading-relaxed text-foreground-700">
                      {item.quote}
                    </blockquote>
                    <figcaption className="mt-5 pt-5 border-t border-background-200 flex items-center gap-3">
                      <span className="w-11 h-11 shrink-0 rounded-full overflow-hidden border border-background-200">
                        <img
                          src={item.avatar}
                          alt={`${item.name}, ${item.role}`}
                          className="w-full h-full object-cover object-top"
                        />
                      </span>
                      <div className="min-w-0">
                        <p className="font-heading text-sm font-semibold text-foreground-950 truncate">{item.name}</p>
                        <p className="text-xs text-foreground-500 truncate">
                          {item.role} · {item.location}
                        </p>
                      </div>
                    </figcaption>
                  </figure>
                </div>
              ))}
            </div>
          </Reveal>

          <div className="mt-6 flex items-center justify-center gap-2">
            {Array.from({ length: pageCount }).map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => scrollToIndex(index)}
                aria-label={`Go to slide ${index + 1}`}
                aria-current={activeIndex === index}
                className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                  activeIndex === index
                    ? 'w-6 bg-accent-500'
                    : 'w-1.5 bg-background-300 hover:bg-secondary-400'
                }`}
              ></button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}